import {DateTime} from "luxon";
import {isSpeciesLevelObservation, type EBirdMyDataSchema} from "ebird-mydata-reader";
import type {CalendarAvailablePeriod, CalendarChecklistLink, CalendarDaySummary, CalendarMonthSummary, CalendarSpeciesSummary, CalendarYearSummary} from "./types";

const CHECKLIST_HEATMAP_WEIGHT = 2;

export const getCalendarHeatmapActivityScore = (day: CalendarDaySummary) => {
    return day.speciesCount + (day.checklistCount * CHECKLIST_HEATMAP_WEIGHT);
};

const getDateKey = (observation: EBirdMyDataSchema) => {
    const date = DateTime.fromISO(observation.date);
    return date.isValid ? date.toISODate() : undefined;
};

const getSpeciesKey = (observation: EBirdMyDataSchema) => `${observation.taxonomicOrder}:${observation.commonName}`;

const compareChecklistLinks = (left: CalendarChecklistLink, right: CalendarChecklistLink) => {
    return (left.time ?? '').localeCompare(right.time ?? '') || left.location.localeCompare(right.location);
};

export const getCalendarAvailablePeriods = (observations: EBirdMyDataSchema[]): CalendarAvailablePeriod[] => {
    const periods = new Map<string, CalendarAvailablePeriod>();

    for (const observation of observations) {
        const date = DateTime.fromISO(observation.date);
        if (!date.isValid) continue;
        periods.set(`${date.year}-${date.month}`, {year: date.year, month: date.month});
    }

    return Array.from(periods.values()).sort((left, right) => left.year - right.year || left.month - right.month);
};

export const getCalendarMonthGridDates = (year: number, month: number) => {
    const monthStart = DateTime.local(year, month, 1).startOf('day');
    const gridStart = monthStart.minus({days: monthStart.weekday % 7});
    const monthEnd = monthStart.endOf('month');
    const trailingDays = 6 - (monthEnd.weekday % 7);
    const gridEnd = monthEnd.plus({days: trailingDays}).startOf('day');
    const dates: string[] = [];

    for (let date = gridStart; date <= gridEnd; date = date.plus({days: 1})) {
        dates.push(date.toISODate());
    }

    return dates;
};

export const getCalendarYearGridDates = (year: number) => {
    const yearStart = DateTime.local(year, 1, 1).startOf('day');
    const gridStart = yearStart.minus({days: yearStart.weekday % 7});
    const yearEnd = DateTime.local(year, 12, 31).startOf('day');
    const gridEnd = yearEnd.plus({days: 6 - (yearEnd.weekday % 7)});
    const dates: string[] = [];

    for (let date = gridStart; date <= gridEnd; date = date.plus({days: 1})) {
        dates.push(date.toISODate());
    }

    return dates;
};

const buildCalendarDaySummary = (date: string, observations: EBirdMyDataSchema[]): CalendarDaySummary => {
    const species = new Map<string, CalendarSpeciesSummary>();
    const liferSpecies = new Map<string, CalendarSpeciesSummary>();
    const checklists = new Map<string, CalendarChecklistLink>();
    const locations = new Set<string>();
    const firstOfYearSpecies = new Set<string>();

    for (const observation of observations) {
        const speciesKey = getSpeciesKey(observation);
        const existingSpecies = species.get(speciesKey);
        const isFirstOfYear = Boolean(observation.isFirstOfYear && isSpeciesLevelObservation(observation));
        species.set(speciesKey, existingSpecies
            ? {...existingSpecies, sightings: existingSpecies.sightings + 1, isFirstOfYear: existingSpecies.isFirstOfYear || isFirstOfYear}
            : {
                taxonomicOrder: observation.taxonomicOrder,
                commonName: observation.commonName,
                scientificName: observation.scientificName,
                sightings: 1,
                isFirstOfYear
            });
        if (observation.locationId || observation.location) locations.add(observation.locationId || observation.location);
        if (observation.isLifer && isSpeciesLevelObservation(observation)) {
            liferSpecies.set(speciesKey, {
                taxonomicOrder: observation.taxonomicOrder,
                commonName: observation.commonName,
                scientificName: observation.scientificName,
                sightings: (liferSpecies.get(speciesKey)?.sightings ?? 0) + 1
            });
        }
        if (isFirstOfYear) firstOfYearSpecies.add(speciesKey);

        const checklistId = observation.submissionId || observation.submissionID;
        if (checklistId) {
            const existingChecklist = checklists.get(checklistId);
            checklists.set(checklistId, existingChecklist
                ? {...existingChecklist, observationCount: existingChecklist.observationCount + 1}
                : {
                    submissionId: checklistId,
                    locationId: observation.locationId,
                    location: observation.location || 'Unknown location',
                    time: observation.time || undefined,
                    speciesCount: 0,
                    observationCount: 1
                });
        }
    }

    for (const checklist of Array.from(checklists.values())) {
        const checklistSpecies = new Set(
            observations
                .filter(observation => (observation.submissionId || observation.submissionID) === checklist.submissionId)
                .map(getSpeciesKey)
        );
        checklist.speciesCount = checklistSpecies.size;
    }

    return {
        date,
        observations,
        speciesCount: species.size,
        checklistCount: checklists.size,
        locationCount: locations.size,
        liferCount: liferSpecies.size,
        firstOfYearCount: firstOfYearSpecies.size,
        liferSpecies: Array.from(liferSpecies.values())
            .sort((left, right) => left.commonName.localeCompare(right.commonName)),
        topSpecies: Array.from(species.values())
            .sort((left, right) => Number(right.isFirstOfYear) - Number(left.isFirstOfYear) || right.sightings - left.sightings || left.commonName.localeCompare(right.commonName))
            .slice(0, 5),
        checklistLinks: Array.from(checklists.values()).sort(compareChecklistLinks)
    };
};

export const buildCalendarYearSummary = (observations: EBirdMyDataSchema[], year: number): CalendarYearSummary => {
    const byDate = new Map<string, EBirdMyDataSchema[]>();

    for (const observation of observations) {
        const date = getDateKey(observation);
        if (!date || DateTime.fromISO(date).year !== year) continue;
        const dayObservations = byDate.get(date) ?? [];
        dayObservations.push(observation);
        byDate.set(date, dayObservations);
    }

    const days = Object.fromEntries(Array.from(byDate.entries()).map(([date, dayObservations]) => [
        date,
        buildCalendarDaySummary(date, dayObservations)
    ]));
    const yearDays = Object.values(days);
    const biggestDay = yearDays.reduce<CalendarDaySummary | undefined>((currentBiggestDay, day) => {
        if (!currentBiggestDay || day.speciesCount > currentBiggestDay.speciesCount) return day;
        return currentBiggestDay;
    }, undefined);

    return {
        year,
        days,
        speciesCount: new Set(yearDays.flatMap(day => day.observations.map(getSpeciesKey))).size,
        liferCount: new Set(yearDays.flatMap(day => day.liferSpecies.map(species => `${species.taxonomicOrder}:${species.commonName}`))).size,
        firstOfYearCount: new Set(yearDays.flatMap(day => day.observations
            .filter(observation => observation.isFirstOfYear && isSpeciesLevelObservation(observation))
            .map(getSpeciesKey))).size,
        biggestDayDate: biggestDay?.date
    };
};

export const buildCalendarMonthSummary = (
    observations: EBirdMyDataSchema[],
    year: number,
    month: number
): CalendarMonthSummary => {
    const visibleDates = new Set(getCalendarMonthGridDates(year, month));
    const byDate = new Map<string, EBirdMyDataSchema[]>();

    for (const observation of observations) {
        const date = getDateKey(observation);
        if (!date || !visibleDates.has(date)) continue;
        const dayObservations = byDate.get(date) ?? [];
        dayObservations.push(observation);
        byDate.set(date, dayObservations);
    }

    const days = Object.fromEntries(Array.from(byDate.entries()).map(([date, dayObservations]) => [
        date,
        buildCalendarDaySummary(date, dayObservations)
    ]));
    const monthDays = Object.values(days).filter(day => DateTime.fromISO(day.date).month === month);
    const biggestDay = monthDays.reduce<CalendarDaySummary | undefined>((currentBiggestDay, day) => {
        if (!currentBiggestDay || day.speciesCount > currentBiggestDay.speciesCount) return day;
        return currentBiggestDay;
    }, undefined);

    return {
        year,
        month,
        days,
        speciesCount: new Set(monthDays.flatMap(day => day.observations.map(getSpeciesKey))).size,
        checklistCount: new Set(monthDays.flatMap(day => day.checklistLinks.map(checklist => checklist.submissionId))).size,
        locationCount: new Set(monthDays.flatMap(day => day.observations.map(observation => observation.locationId || observation.location))).size,
        liferCount: new Set(monthDays.flatMap(day => day.observations
            .filter(observation => observation.isLifer && isSpeciesLevelObservation(observation))
            .map(getSpeciesKey))).size,
        biggestDayDate: biggestDay?.date
    };
};
